import type { AppState } from './models';

export type CertificateRarity = '일반' | '성장' | '특별' | '전설';
export type CertificateTone = 'ice' | 'blue' | 'gold' | 'violet' | 'mint' | 'rose';
export type CertificateCharacter =
  | 'haneul-record' | 'haneul-saving' | 'haneul-think' | 'haneul-heart'
  | 'haneul-run' | 'haneul-openarms' | 'haneul-celebrate' | 'haneul-star'
  | 'sage-research' | 'sage-binocular' | 'sage-checklist' | 'arctic-fox';

export interface CertificateDefinition {
  id: string;
  order: number;
  category: string;
  title: string;
  subtitle: string;
  shortTitle: string;
  description: string;
  achievementCopy: string;
  unlockRule: string;
  rarity: CertificateRarity;
  tone: CertificateTone;
  rewardXp: number;
  badgeAsset: string;
  character: CertificateCharacter;
  sealLabel: string;
  setId: CertificateSetId;
  test(state: AppState): boolean;
}

export type CertificateSetId = 'habit' | 'knowledge' | 'together' | 'mastery';

export interface CertificateSetDefinition {
  id: CertificateSetId;
  title: string;
  description: string;
  certificateIds: string[];
  rewardXp: number;
  frameName: string;
}

const A = '/assets';
const savingRecords = (state: AppState) => state.records.filter((record) => record.type === 'saving').length;
const approvedMissions = (state: AppState) => state.missions.filter((mission) => mission.status === 'approved').length;
const completedChapters = (state: AppState) => state.learningProgress.filter((progress) => progress.completed).length;
const approvedVisits = (state: AppState) => state.passportVisits.filter((visit) => visit.status === 'approved').length;
const unlockedItems = (state: AppState) => state.collectionItems.filter((item) => item.unlockedAt).length;
const completedFamilyMissions = (state: AppState) => state.familyMissionChallenges.filter((mission) => ['submitted', 'approved'].includes(mission.status)).length;

export const CERTIFICATE_CATALOG: CertificateDefinition[] = [
  {
    id: 'first-snow-record', order: 1, category: '돈 기록', shortTitle: '첫눈 기록', title: '첫눈 기록 수료증',
    subtitle: 'Certificate of the First Money Record', description: '받고, 쓰고, 모은 돈을 처음으로 직접 기록한 선택을 인증합니다.',
    achievementCopy: '자신의 돈 흐름을 관찰하고 기억하는 첫 번째 습관을 만들었습니다.', unlockRule: '돈 기록 1회 완료', rarity: '일반', tone: 'ice', rewardXp: 20,
    badgeAsset: `${A}/badges/mrm_badge_11_firstrecord_v01.png`, character: 'haneul-record', sealLabel: 'FIRST RECORD', setId: 'habit',
    test: (state) => state.records.length >= 1,
  },
  {
    id: 'saving-snowball', order: 2, category: '저축 미션', shortTitle: '저축 새싹', title: '저축 스노우볼 수료증',
    subtitle: 'Certificate of the Saving Snowball', description: '작은 금액이라도 목표를 향해 먼저 떼어 놓은 선택을 인증합니다.',
    achievementCopy: '현재의 소비보다 미래의 목표를 먼저 떠올리는 힘을 키웠습니다.', unlockRule: '저축 기록 1회 또는 목표 저축 시작', rarity: '성장', tone: 'blue', rewardXp: 30,
    badgeAsset: `${A}/badges/mrm_badge_14_goalhalf_v01.png`, character: 'haneul-saving', sealLabel: 'SAVING', setId: 'habit',
    test: (state) => savingRecords(state) >= 1 || state.goal.currentAmount > 0,
  },
  {
    id: 'patience-choice', order: 3, category: '선택 미션', shortTitle: '기다림 선택', title: '기다림의 선택 수료증',
    subtitle: 'Certificate of the Patient Choice', description: '사고 싶은 마음을 바로 행동으로 옮기지 않고 한 번 더 생각한 선택을 인증합니다.',
    achievementCopy: '빠른 선택보다 나에게 맞는 선택을 찾는 힘을 길렀습니다.', unlockRule: '기다림 미션 승인 또는 기다림 컬렉션 해금', rarity: '성장', tone: 'rose', rewardXp: 35,
    badgeAsset: `${A}/badges/mrm_badge_12_waitingsuccess_v01.png`, character: 'haneul-think', sealLabel: 'PATIENCE', setId: 'habit',
    test: (state) => state.missions.some((mission) => mission.status === 'approved' && mission.title.includes('기다'))
      || state.collectionItems.some((item) => item.id === 'collect_patience_cookie' && Boolean(item.unlockedAt)),
  },
  {
    id: 'price-detective', order: 4, category: '비교 미션', shortTitle: '가격 탐정', title: '현명한 가격 탐정 수료증',
    subtitle: 'Certificate of the Smart Price Detective', description: '가격뿐 아니라 양·품질·필요성을 함께 비교한 행동을 인증합니다.',
    achievementCopy: '숫자 하나만 보지 않고 선택의 기준을 세우는 힘을 키웠습니다.', unlockRule: '가격 비교 미션 승인 또는 전통시장 패스포트 승인', rarity: '성장', tone: 'gold', rewardXp: 40,
    badgeAsset: `${A}/badges/mrm_badge_13_pricecompare_v01.png`, character: 'sage-binocular', sealLabel: 'COMPARE', setId: 'knowledge',
    test: (state) => state.missions.some((mission) => mission.status === 'approved' && mission.title.includes('가격'))
      || state.passportVisits.some((visit) => visit.status === 'approved' && visit.placeId.includes('market')),
  },
  {
    id: 'economy-weather', order: 5, category: '경제기사', shortTitle: '경제기상 관찰자', title: '경제기상 관찰자 수료증',
    subtitle: 'Certificate of the Economy Weather Observer', description: '경제기사를 읽고 세상의 변화를 자신의 언어로 이해한 학습을 인증합니다.',
    achievementCopy: '경제의 변화를 막연히 두려워하지 않고 관찰하고 질문하는 힘을 키웠습니다.', unlockRule: '경제기사 퀴즈 1회 완료', rarity: '특별', tone: 'blue', rewardXp: 50,
    badgeAsset: `${A}/badges/mrm_badge_02_missionrewardstar_v01.png`, character: 'sage-research', sealLabel: 'ECONOMY', setId: 'knowledge',
    test: (state) => state.quizAttempts.some((attempt) => attempt.childId === state.child.id),
  },
  {
    id: 'magic-page-restorer', order: 6, category: '경제도서관', shortTitle: '페이지 복원가', title: '마법 경제 페이지 복원 수료증',
    subtitle: 'Certificate of the Magic Page Restorer', description: '경제도서관의 이야기를 끝까지 탐험하고 하나의 지식 페이지를 복원한 성취를 인증합니다.',
    achievementCopy: '경제의 역사와 개념을 자신의 선택과 연결하는 힘을 키웠습니다.', unlockRule: '경제도서관 챕터 1개 완주', rarity: '특별', tone: 'violet', rewardXp: 60,
    badgeAsset: `${A}/badges/mrm_badge_02_missionrewardstar_v01.png`, character: 'arctic-fox', sealLabel: 'LIBRARY', setId: 'knowledge',
    test: (state) => completedChapters(state) >= 1,
  },
  {
    id: 'family-expedition', order: 7, category: '가족 미션', shortTitle: '가족 탐험대', title: '가족 탐험대 수료증',
    subtitle: 'Certificate of the Family Expedition', description: '가족과 함께 돈에 관해 이야기하고 공동 미션을 실천한 경험을 인증합니다.',
    achievementCopy: '혼자 정답을 찾기보다 서로의 이유를 듣고 함께 약속하는 힘을 키웠습니다.', unlockRule: '가족 미션 1회 제출 또는 승인', rarity: '특별', tone: 'rose', rewardXp: 70,
    badgeAsset: `${A}/badges/mrm_badge_01_patiencesnowflake_v01.png`, character: 'haneul-heart', sealLabel: 'FAMILY', setId: 'together',
    test: (state) => completedFamilyMissions(state) >= 1,
  },
  {
    id: 'passport-explorer', order: 8, category: '현장 미션', shortTitle: '금융 패스포트', title: '현실 금융 탐험 수료증',
    subtitle: 'Certificate of the Real-world Finance Explorer', description: '시장·금융기관·박물관 등 현실 공간에서 경제의 원리를 직접 관찰한 경험을 인증합니다.',
    achievementCopy: '앱 안의 지식을 현실의 행동과 관찰로 연결했습니다.', unlockRule: '금융 패스포트 방문 1회 승인', rarity: '특별', tone: 'mint', rewardXp: 80,
    badgeAsset: `${A}/badges/mrm_badge_13_pricecompare_v01.png`, character: 'haneul-run', sealLabel: 'PASSPORT', setId: 'together',
    test: (state) => approvedVisits(state) >= 1,
  },
  {
    id: 'fridge-curator', order: 9, category: '냥장고 미션', shortTitle: '기억 보관가', title: '시간의 냥장고 보관 수료증',
    subtitle: 'Certificate of the Memory Fridge Curator', description: '기록·미션·가족의 응원을 냥장고에 모아 성장의 기억으로 보관한 성취를 인증합니다.',
    achievementCopy: '결과만 보는 대신 과정과 의미를 보존하는 힘을 키웠습니다.', unlockRule: '냥장고 컬렉션 4종 해금', rarity: '특별', tone: 'ice', rewardXp: 90,
    badgeAsset: `${A}/badges/mrm_badge_01_patiencesnowflake_v01.png`, character: 'haneul-openarms', sealLabel: 'MEMORY', setId: 'together',
    test: (state) => unlockedItems(state) >= 4,
  },
  {
    id: 'basecamp-30', order: 10, category: '30일 성장', shortTitle: '베이스캠프 완주', title: '30일 베이스캠프 완주 수료증',
    subtitle: 'Certificate of the 30-day Basecamp', description: '30일 동안 기록·학습·미션을 반복하며 자신만의 돈 습관 리듬을 만든 성취를 인증합니다.',
    achievementCopy: '완벽함보다 꾸준히 다시 시작하는 힘을 증명했습니다.', unlockRule: '30일 성장보드 완주', rarity: '특별', tone: 'gold', rewardXp: 120,
    badgeAsset: `${A}/badges/mrm_badge_15_steadypractice_locked_v01.png`, character: 'haneul-celebrate', sealLabel: '30 DAYS', setId: 'mastery',
    test: (state) => state.growthSummary.completedBoardDays >= 30,
  },
  {
    id: 'world-canon-keeper', order: 11, category: '세계관 탐험', shortTitle: '세계관 수호자', title: '스노우볼 세계관 수호자 수료증',
    subtitle: 'Certificate of the Snowball World Keeper', description: '스노우 마운틴의 법칙과 경제도서관의 이야기를 깊이 탐험한 학습 성취를 인증합니다.',
    achievementCopy: '돈의 크기보다 선택의 깊이가 성장의 기준이라는 원칙을 이해했습니다.', unlockRule: '경제도서관 5개 챕터 완주와 배지 8개', rarity: '전설', tone: 'violet', rewardXp: 160,
    badgeAsset: `${A}/badges/mrm_badge_02_missionrewardstar_v01.png`, character: 'sage-checklist', sealLabel: 'WORLD KEEPER', setId: 'mastery',
    test: (state) => completedChapters(state) >= 5 && state.badges.length >= 8,
  },
  {
    id: 'snowball-master', order: 12, category: '통합 성장', shortTitle: '스노우볼 마스터', title: '스노우볼 마스터 인증서',
    subtitle: 'Master Certificate of MY real Money', description: '기록·저축·경제학습·가족·현장 탐험을 연결해 자신의 금융 성장 서사를 완성한 최고 성취를 인증합니다.',
    achievementCopy: '자신의 선택을 이해하고, 행동하고, 회고하며 다른 사람과 나누는 힘을 증명했습니다.', unlockRule: '수료증 11종의 조건 모두 달성', rarity: '전설', tone: 'gold', rewardXp: 250,
    badgeAsset: `${A}/certificates/mrm_cert_02_goldmedal_v01.png`, character: 'haneul-star', sealLabel: 'MASTER', setId: 'mastery',
    test: (state) => CERTIFICATE_CATALOG.filter((item) => item.id !== 'snowball-master').every((item) => item.test(state)),
  },
];

export const CERTIFICATE_SETS: CertificateSetDefinition[] = [
  { id: 'habit', title: '작은 습관 컬렉션', description: '기록·저축·기다림으로 만드는 첫 번째 스노우볼', certificateIds: ['first-snow-record','saving-snowball','patience-choice'], rewardXp: 60, frameName: '첫눈 실버 프레임' },
  { id: 'knowledge', title: '경제 탐구 컬렉션', description: '비교·경제기사·도서관에서 키운 판단력', certificateIds: ['price-detective','economy-weather','magic-page-restorer'], rewardXp: 90, frameName: '지혜의 딥블루 프레임' },
  { id: 'together', title: '가족·현실 탐험 컬렉션', description: '가족과 현실 세계에서 살아낸 금융 경험', certificateIds: ['family-expedition','passport-explorer','fridge-curator'], rewardXp: 120, frameName: '가족 눈꽃 골드 프레임' },
  { id: 'mastery', title: '정상 정복 컬렉션', description: '꾸준함·세계관 이해·통합 성장을 완성한 기록', certificateIds: ['basecamp-30','world-canon-keeper','snowball-master'], rewardXp: 200, frameName: '스노우 마운틴 전설 프레임' },
];

export interface CertificateClaimState {
  claims: Record<string, string>;
  setRewards: Record<string, string>;
}

export const EMPTY_CERTIFICATE_CLAIMS: CertificateClaimState = { claims: {}, setRewards: {} };

export function certificateClaimStorageKey(childId: string): string {
  return `mrm.certificate.collection.release2.1.${childId}`;
}

export function loadCertificateClaims(childId: string): CertificateClaimState {
  if (typeof window === 'undefined') return EMPTY_CERTIFICATE_CLAIMS;
  try {
    const raw = window.localStorage.getItem(certificateClaimStorageKey(childId));
    if (!raw) return EMPTY_CERTIFICATE_CLAIMS;
    const value = JSON.parse(raw) as Partial<CertificateClaimState>;
    return {
      claims: value.claims && typeof value.claims === 'object' ? value.claims : {},
      setRewards: value.setRewards && typeof value.setRewards === 'object' ? value.setRewards : {},
    };
  } catch {
    return EMPTY_CERTIFICATE_CLAIMS;
  }
}

export function saveCertificateClaims(childId: string, value: CertificateClaimState): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(certificateClaimStorageKey(childId), JSON.stringify(value));
}

export function certificateById(id: string | undefined): CertificateDefinition | undefined {
  return CERTIFICATE_CATALOG.find((item) => item.id === id);
}

export function claimedCertificateCount(claims: CertificateClaimState): number {
  return Object.keys(claims.claims).filter((id) => CERTIFICATE_CATALOG.some((item) => item.id === id)).length;
}

export function setComplete(set: CertificateSetDefinition, claims: CertificateClaimState): boolean {
  return set.certificateIds.every((id) => Boolean(claims.claims[id]));
}

export function unlockedCertificateCount(state: AppState): number {
  return CERTIFICATE_CATALOG.filter((item) => item.test(state)).length;
}
